import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { useTokensPrice } from '~/hooks/useTokenPrice'
import { useLimitSwapTokens } from '~/state/dex/hooks'
import { useTokenBalances } from '~/state/wallet/hooks'
import { FixedPointNumber } from '~/utils/fixedPoint'
import { getAddress } from 'ethers/lib/utils'
import {
  LimitOrderStatus,
  useUserLimitOrderList,
} from '~/hooks/useUserLimitOrderList'

export interface OrderedLimitSwapToken extends LimitSwapToken {
  balance?: FixedPointNumber
  price?: string
  priceUSD?: string
}

const HistoryOrderStatuses = [
  LimitOrderStatus.Fulfilled,
  LimitOrderStatus.Cancelled,
  LimitOrderStatus.Expired,
]

export const useOrderedLimitSwapTokens = (
  selectedChainId: number | null,
): OrderedLimitSwapToken[] => {
  const { address: account, chainId: currentChainId } = useAccount()
  const isSameChain = currentChainId === selectedChainId
  const tokens = useLimitSwapTokens(selectedChainId)

  const tokenBalances = useTokenBalances(
    isSameChain ? Object.values(tokens) : [],
  )

  const prices = useTokensPrice(isSameChain ? Object.keys(tokens) : [])

  return useMemo(() => {
    let orderedTokenListBySymbol: OrderedLimitSwapToken[] = []
    orderedTokenListBySymbol = Object.values(tokens)
      .filter((token) => token.symbol != null)
      .sort((a, b) => {
        const s1 = a.symbol.toUpperCase()
        const s2 = b.symbol.toUpperCase()
        return s1 > s2 ? 1 : s1 === s2 ? 0 : -1
      })

    if (account && isSameChain) {
      let orderedTokenList = orderedTokenListBySymbol.map((token) => {
        const address = getAddress(token.address)
        const priceUSD = new BigNumber(prices[address] || 0)
          .multipliedBy(tokenBalances[token.address]?.toDecimalsFixed() || 0)
          .toFixed(2)
        return {
          ...token,
          balance: tokenBalances[token.address] || new FixedPointNumber(0),
          price: prices[address],
          priceUSD: isNaN(Number(priceUSD)) ? '0' : priceUSD,
        }
      })
      return orderedTokenList.sort(
        (a, b) => Number(b.priceUSD) - Number(a.priceUSD),
      )
    } else return orderedTokenListBySymbol
  }, [prices, tokenBalances, tokens, account, isSameChain, selectedChainId])
}

export const useOrderedLimitSwapTokensByRecent = (
  selectedChainId: number | null,
): OrderedLimitSwapToken[] => {
  const orderedTokensByValue = useOrderedLimitSwapTokens(selectedChainId)
  const { data: ordersHistory } = useUserLimitOrderList(HistoryOrderStatuses)

  return useMemo(() => {
    if (!ordersHistory) return orderedTokensByValue

    const recentTokens = {}
    ordersHistory.forEach((order) => {
      const fromToken = order.makerToken
      const toToken = order.takerToken
      recentTokens[fromToken] = recentTokens[fromToken]
        ? recentTokens[fromToken] + 1
        : 1
      recentTokens[toToken] = recentTokens[toToken]
        ? recentTokens[toToken] + 1
        : 1
    })

    const orderedTokensByRecent = orderedTokensByValue
      .map((token) => {
        return {
          ...token,
          tradeCounts: recentTokens[token.address] || 0,
        }
      })
      .sort((a, b) => b.tradeCounts - a.tradeCounts)

    return orderedTokensByRecent
  }, [orderedTokensByValue, ordersHistory])
}
